/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.2.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
 * Copyright 2023 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

// prop-type is a library for typechecking of props
import PropTypes from 'prop-types'
import classNames from 'classnames'

// formik components
import { Field, getIn, useFormikContext } from 'formik'
import { serverErrorOf } from 'components/forms/serverErrors'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDInput from 'components/MDInput'

import PendingBadge from 'components/contacts/PendingBadge'
import './FormField.styles.scss'
import { Autocomplete, Box } from '@mui/material'

const TEXT_FIELD_PADDING = 12
const AUTOCOMPLETE_INDICATORS_WIDTH = 64

function FormField({
    label,
    name,
    type,
    disabled,
    options,
    className,
    isInvalid,
    validationMessage,
    onChoose,
    pending,
    ...rest
}) {

    const {
        values,
        errors,
        touched,
        status,
        setFieldValue,
    } = useFormikContext()

    const clientError = getIn(touched, name) ? getIn(errors, name) : undefined
    const serverError = serverErrorOf(status, values, name)
    const shownError = clientError ?? serverError

    const classes = classNames({
        'form-field': true,
        ...className,
    })

    const pendingMark = (insetInlineEnd) => pending ? (
        <Box
            component="span"
            sx={{
                position: 'absolute',
                insetInlineEnd,
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                display: 'flex',
            }}
        >
            <PendingBadge testId={`field-pending-${name}`} />
        </Box>
    ) : null

    const createField = () => {
        if (type === 'select' || options) {
            const optionValues = options.map((option) => option.value)
            return (
                <Autocomplete
                    // options={values[name] ? [values[name], ...optionValues] : optionValues}
                    options={optionValues}
                    getOptionLabel={
                        (value) => {
                            const option = options.find((option) => option.value === value)
                            const label = option?.label || 'נא לבחור ערך'
                            // return `${value}: ${label}`
                            return label
                        }
                    }
                    isOptionEqualToValue={(option, value) => {
                        return value === '' || option === value
                    }}
                    value={values[name]}
                    onChange={(e, value) => {
                        setFieldValue(name, value)
                        onChoose?.(name, value)
                    }}
                    slotProps={{
                        clearIndicator: {
                            'data-testid': `field-clear-${name}`,
                        },
                    }}
                    renderOption={(optionProps, optionValue) => (
                        <li
                            {...optionProps}
                            key={optionValue}
                            data-testid={`field-option-${name}-${optionValue}`}
                        >
                            {options.find((option) => option.value === optionValue)?.label}
                        </li>
                    )}
                    renderInput={(params) => (
                        <Field
                            {...rest}
                            {...params}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {pendingMark(AUTOCOMPLETE_INDICATORS_WIDTH)}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            inputProps={{
                                ...params.inputProps,
                                'data-testid': `field-${name}`,
                            }}
                            name={name}
                            type={'select'}
                            disabled={disabled}
                            as={MDInput}
                            label={label}
                            fullWidth
                            className={classes}
                        />
                    )}
                />
            )
        }
        else {
            return (
                <Field
                    {...rest}
                    InputProps={{ endAdornment: pendingMark(TEXT_FIELD_PADDING) }}
                    inputProps={{ 'data-testid': `field-${name}` }}
                    name={name}
                    type={type}
                    disabled={disabled}
                    as={MDInput}
                    label={label}
                    fullWidth
                    className={classes}
                />
            )
        }
    }

    const field = createField()

    return (
        <MDBox mb={1.5}>
            {field}
            <MDBox mt={0.75}>
                <MDTypography
                    component="div"
                    variant="caption"
                    color="error"
                    fontWeight="regular"
                    data-testid={`field-error-${name}`}
                >
                    {shownError}
                </MDTypography>
            </MDBox>
        </MDBox>
    )
}

// typechecking props for FormField
FormField.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
}

export default FormField
